import {AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Modal} from 'bootstrap';

export type PaymentBlockOutcome = 'processing' | 'success' | 'failure';

/**
 * "Payment under processing" popup (CR-2026-30 Part B). Only dismissible through an explicit
 * in-modal action - backdrop click and ESC are disabled (backdrop: 'static', keyboard: false) so
 * it can't be dismissed by accident while the duplicate-payment guard (enforced server-side via
 * a Redis block on every payment endpoint) is still active. While `outcome` is 'processing' the
 * header close button behaves like "Go to Home" (the only explicit exit while still blocked);
 * once it's 'success' or 'failure' both the close button and "Okay" just dismiss the popup.
 */
@Component({
  selector: 'app-payment-block-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './payment-block-modal.component.html',
  styleUrl: './payment-block-modal.component.scss'
})
export class PaymentBlockModalComponent implements AfterViewInit {
  @Input() remainingSeconds = 0;
  @Input() outcome: PaymentBlockOutcome = 'processing';
  @Input() outcomeMessage = '';
  @Output() goHome = new EventEmitter<void>();
  @Output() acknowledge = new EventEmitter<void>();

  @ViewChild('modalRef', { static: true }) modalRef!: ElementRef;
  private modalInstance!: Modal;

  ngAfterViewInit(): void {
    this.modalInstance = new Modal(this.modalRef.nativeElement, {
      backdrop: 'static',
      keyboard: false
    });
  }

  showModal(): void {
    this.modalInstance?.show();
  }

  hideModal(): void {
    this.modalInstance?.hide();
  }

  onCloseClick(): void {
    if (this.outcome === 'processing') {
      this.onGoHome();
    } else {
      this.onAcknowledge();
    }
  }

  onGoHome(): void {
    this.goHome.emit();
    this.hideModal();
  }

  onAcknowledge(): void {
    this.acknowledge.emit();
    this.hideModal();
  }

  get timeLabel(): string {
    const safeSeconds = Math.max(0, this.remainingSeconds);
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
